import {useMediaQuery} from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useLocation} from 'react-router-dom'
import {
  ApplePayContextType,
  GetAutoCharge,
  GetOptions,
  isMaxPayOrder,
  MaxPayContextType,
  PaymentMethodType,
  PaymentOrder,
  PaymentProviderType,
  PaymentStatus,
  SepaContextType,
  SepaProviderType,
  setConfirmPayModal,
  setPackageModalOpen,
  setPaymentModal,
  TokenType,
  useConfirmPayModal
} from 'entities/payment'
import {names} from 'shared/lib'
import {redirectToOtherSite} from 'shared/lib/links'
import {scrollToTop} from 'shared/lib/scroll-to'
import {ErrorResponsesType} from 'shared/types'
import {HorizontalDivider} from 'shared/ui/dividers/horizontalDivider'
import {
  ArrowLeft,
  Close,
  MasterCardCheck,
  VisaSecureBlur
} from 'shared/ui/icons'
import {Modal} from 'shared/ui/modal'
import {Toggle} from 'shared/ui/toggle'
import {Bank, BitcoinIcon, PciCertificateIcon, PlusIcon} from './img/icons'
import styles from './styles.module.scss'
import {ApplePayButton} from './ui/applePayButton'
import {CardSelect} from './ui/cardSelect'
import {ConfirmButton} from './ui/confirmButton'
import {CurrentPackage} from './ui/currentPackage'
import {InfoBlock} from './ui/infoBlock'
import {PayButton} from './ui/payButton'
import {PayForm} from './ui/payForm'
import {PayFrame} from './ui/payFrame'

const GetPayments = async () => {
  try {
    const {data} = await GetOptions()
    const maxpayTokens = data.find((item) => item.provider === PaymentProviderType.MAXPAY && item.method === PaymentMethodType.TOKEN)
    const corefyTokens = data.find((item) => item.provider === PaymentProviderType.COREFY && item.method === PaymentMethodType.TOKEN)
    const isMaxPay = Boolean(data.find((item) => item.provider === PaymentProviderType.MAXPAY && item.method === PaymentMethodType.CARD))
    const isCrypto = Boolean(data.find((item) => item.provider === PaymentProviderType.COIN_PAYMENTS && item.method === PaymentMethodType.CRYPTO))
    const sepa = data.find((item) => item.provider === PaymentProviderType.NODA && item.method === PaymentMethodType.SEPA)
    const sepaContext = sepa?.context as unknown as SepaContextType
    const apple = data.find((item) => item.provider === PaymentProviderType.COREFY && item.method === PaymentMethodType.APPLE_PAY)
    const appleContext = apple?.context as unknown as ApplePayContextType
    const corefy = data.find((item) => item.provider === PaymentProviderType.COREFY && item.method === PaymentMethodType.CARD)

    const tokens: TokenType[] = []
    if (maxpayTokens?.context) tokens.push(...maxpayTokens.context.map((item) => ({...item, provider: maxpayTokens.provider})) as TokenType[])
    if (corefyTokens?.context) tokens.push(...corefyTokens.context.map((item) => ({...item, provider: corefyTokens.provider})) as TokenType[])

    return {
      tokens,
      isMaxPay,
      isCrypto,
      isSepa: sepa,
      isCorefy: corefy,
      sepaProviders: sepaContext?.options.slice(0, 5),
      isApplePay: Boolean(apple),
      appleRequestData: {...appleContext?.merchant}
    }
  } catch (e) {
    console.error(e)
  }
}

let interval: NodeJS.Timeout

export const ConfirmPayModal: FC = () => {
  const dispatch = useDispatch()
  const {isOpen, ...openPackage} = useConfirmPayModal()
  const [iframeId, setIframeId] = useState('')
  const [selectedToken, setSelectedToken] = useState('')
  const [tokens, setTokens] = useState<TokenType[]>([])
  const [newMethods, setNewMethods] = useState({
    [PaymentMethodType.CARD]: false,
    [PaymentMethodType.SEPA]: false,
    [PaymentMethodType.CRYPTO]: false,
    [PaymentProviderType.MAXPAY]: false,
    [PaymentMethodType.APPLE_PAY]: false,
    sepaProviders: [] as SepaProviderType[],
    appleRequestData: {} as ApplePayJS.ApplePayPaymentRequest
  })
  const [isNewMethod, setIsNewMethod] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isAutomaticTopUp, setIsAutomaticTopUp] = useState(false)
  const [isShowAutomatic, setIsShowAutomatic] = useState(false)
  const [isIframeError, setIsIframeError] = useState(false)
  const withSale = openPackage.originPrice && openPackage.originPrice !== openPackage.price
  const withTokens = useMemo(() => Boolean(tokens.length), [tokens.length])
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const refScroll = useRef<HTMLDivElement | null>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const maxPayRef = useRef<HTMLDivElement | null>(null)
  const location = useLocation()

  const onClose = () => dispatch(setConfirmPayModal({isOpen: false}))

  const onErrorHandler = (e: unknown) => {
    const typedError = e as ErrorResponsesType
    dispatch(setPaymentModal({
      isOpen: true,
      status: PaymentStatus.FAILED,
      error: typedError.error || typedError.message || typedError.toString()
    }))
  }

  const addNewCardHandler = async (newMethod?: boolean) => {
    if (newMethods[PaymentMethodType.CARD]) {
      return setIsNewMethod(true)
    }

    scrollToTop(refScroll)
    setIsNewMethod(Boolean(newMethod))
    setIsLoading(true)

    try {
      const order = await PaymentOrder({
        provider: PaymentProviderType.MAXPAY,
        method: PaymentMethodType.CARD,
        packageId: openPackage.id
      })

      if (!order.data.context) throw Error('No context')
      const context = order.data.context as MaxPayContextType
      setIframeId(`psp-hpp-${context.signature}`)


      const script = document.createElement('script')

      script.src = 'https://hpp.maxpay.com/paymentPage.js'
      script.async = true

      script.className = 'pspScript'
      for (const scriptKey in context) {
        const key = scriptKey as keyof MaxPayContextType
        const value = key === 'customproduct' ? JSON.stringify(context[key]) : context[key]

        script.setAttribute(`data-${scriptKey}`, value)
      }
      //init script
      script.onload = () => {
        //fix for safari
        setTimeout(() => window.dispatchEvent(new Event('change')), 1000)
      }

      maxPayRef.current?.insertBefore(script, maxPayRef.current?.firstChild)

      let counter = 0

      interval = setInterval(() => {
        //clear if not loaded
        if (counter >= 10 && isOpen) {
          setIsIframeError(true)
          clearInterval(interval)
          setIsLoading(false)
          return
        }

        const frame = document.querySelector(`iframe[id*='psp-hpp']`) as HTMLIFrameElement
        if (!frame) return
        //check src for loading
        const isLoaded = Boolean(frame.src)
        if (!isLoaded) return counter++

        setTimeout(() => {
          if (!isOpen) return

          setIsLoading(false)
          clearInterval(interval)
        }, 1000)

      }, 1000)
    } catch (e) {
      setIsLoading(false)
      setIsIframeError(true)
    }
  }

  const openPaymentNewTab = async (isCard?: boolean) => {
    const order = await PaymentOrder({
      provider: PaymentProviderType.NODA,
      method: isCard ? PaymentMethodType.CARD : PaymentMethodType.SEPA,
      packageId: openPackage.id
    })
    if (isMaxPayOrder(order.data.context)) return

    const redirectURL = order.data.context?.redirectUrl
    if (!redirectURL) throw Error('No redirect URL')
    redirectToOtherSite(redirectURL)
  }

  const instantPayHandler = async (isCard?: boolean) => {
    try {
      dispatch(setPaymentModal({isOpen: true, status: PaymentStatus.SEPA_IN_PROGRESS}))
      onCloseHandler()
      await openPaymentNewTab(isCard)
    } catch (e) {
      onErrorHandler(e)
    }
  }

  const confirmPayHandler = useCallback(() => {
    const provider = tokens.find(item=> item.id === selectedToken)?.provider
    if (!provider) return onErrorHandler('No selected token')

    dispatch(setPaymentModal({isOpen: true, status: PaymentStatus.IN_PROGRESS}))
    onCloseHandler()
    dispatch(setPackageModalOpen(false))


    PaymentOrder({
      provider,
      packageId: openPackage.id,
      method: PaymentMethodType.TOKEN,
      idToken: selectedToken,
      autoCharge: isAutomaticTopUp
    }).catch(onErrorHandler)

  }, [openPackage, selectedToken])

  const cryptoPayHandler = () => {
    const data = JSON.stringify({
      redirectPath: location.pathname,
      id: openPackage.id,
      price:openPackage.price
    })
    redirectToOtherSite(`${window.location.origin}/crypto-pay/${btoa(data)}`)
    dispatch(setPaymentModal({isOpen: true, status: PaymentStatus.SEPA_IN_PROGRESS}))
    onCloseHandler()
  }

  const dropState = () => {
    clearInterval(interval)
    setIsLoading(true)
    setIsNewMethod(false)
    setIsIframeError(false)
  }

  const backHandler = () => {
    dropState()
    setIsLoading(false)
  }

  const onCloseHandler = () => {
    dropState()

    if (popupRef.current && is768p) {
      popupRef.current.style.left = '100%'
      return setTimeout(onClose, Number(styles.animateCloseModalTimeMs))
    }

    onClose()
  }

  const changeTokenHandler = (tokenId: string) => setSelectedToken(tokenId)

  const PaymentButtons = useMemo(() => () => (
    <>
      <ApplePayButton
        isExist={newMethods[PaymentMethodType.APPLE_PAY]}
        context={newMethods.appleRequestData}
        idPackage={openPackage.id}
      />
      <PayButton
        backgroundColor='#2C2E2F'
        handler={() => instantPayHandler()}
        isVisible={newMethods[PaymentMethodType.SEPA]}
      >
        <Bank /> Bank Pay
      </PayButton>
      <PayButton
        backgroundColor='#7C3FFF'
        handler={cryptoPayHandler}
        isVisible={newMethods[PaymentMethodType.CRYPTO]}
      >
        <BitcoinIcon /> Crypto Pay
      </PayButton>
    </>
  ), [newMethods, instantPayHandler, cryptoPayHandler])

  useEffect(() => {
    if (!isOpen) return

    dropState()
    onClose()
  }, [location])

  useEffect(() => {
    if (!isOpen) return dropState()
    setTokens([])

    GetAutoCharge()
      .then(({data}) => setIsShowAutomatic(!Boolean(data)))
      .catch(console.error)

    GetPayments()
      .then((data) => {
        if (!data) return
        if (data.isSepa) setNewMethods((prevState) => ({
          ...prevState,
          [PaymentMethodType.SEPA]: true,
          sepaProviders: data.sepaProviders
        }))
        if (data.isCorefy) {
          setNewMethods((prevState) => ({...prevState, [PaymentMethodType.CARD]: true}))
          setIsLoading(false)
        }
        if (data.isCrypto) setNewMethods((prevState) => ({...prevState, [PaymentMethodType.CRYPTO]: true}))

        if (data.isMaxPay) setNewMethods((prevState) => ({...prevState, [PaymentProviderType.MAXPAY]: true}))
        if (data.isApplePay) {
          data.appleRequestData.total = {label: openPackage.name, amount: openPackage.originPrice.toString()}
          setNewMethods((prevState) => ({
            ...prevState,
            [PaymentMethodType.APPLE_PAY]: true,
            appleRequestData: data.appleRequestData as ApplePayJS.ApplePayPaymentRequest
          }))
        }
        //todo delete after switch to corefy
        if (!data.tokens?.length && data.isMaxPay && !data.isCorefy) {
          setNewMethods((prevState) => ({...prevState, [PaymentProviderType.MAXPAY]: false}))
          return addNewCardHandler(false)
        }

        const tokens = data.tokens as TokenType[]
        setTokens(tokens)
        //last used token (sorting on backend)
        setSelectedToken(tokens[0].id)
        setIsLoading(false)
      })
      .catch(console.error)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const timeout = setTimeout(() => {
      if (popupRef.current) {
        popupRef.current.style.left = '0'
      }
    }, Number(styles.animateCloseModalTimeMs))

    return () => {
      clearTimeout(timeout)
    }
  }, [isOpen])

  return (
    <Modal onClose={onCloseHandler} disableEnforceFocus open={isOpen} classes={{backdrop: styles.modalBackdrop}}>
      <div className={styles.confirmPayModal}>
        <div className={styles.wrap} ref={popupRef}>
          <div
            className={`
            ${styles.containerWrapper}
            ${styles.customScroll}
            ${!isNewMethod ? styles.step1 : styles.step2}
          `}
            onClick={onCloseHandler}
          >
            <div className={styles.container} onClick={(e) => e.stopPropagation()}>
              <div className={styles.confirmContainer}>
                <div className={styles.confirmHeader}>
                  <div className={styles.header}>
                    {isNewMethod || is768p ?
                      <div className={styles.backBtn} onClick={isNewMethod ? backHandler : onCloseHandler}>
                        <ArrowLeft />
                      </div> : null}
                    <h2 className={styles.title}>{isNewMethod ? 'Add new method' : 'Confirm and pay'}</h2>
                  </div>
                  <div onClick={onCloseHandler} className={styles.closeBtn}><Close /></div>
                </div>
                <div className={names(styles.content, styles.customScroll)} ref={refScroll}>
                  <div className={styles.contentMain}>
                    {isLoading ?
                      <div className={styles.skeleton}>
                        <Skeleton animation='wave' style={{height: '100%'}} />
                      </div>
                      : null}
                    <div className={styles.payments} style={{visibility: isLoading ? 'hidden' : 'visible'}}>
                      {
                        !isNewMethod ?
                          <>
                            <div className={styles.payments__cards}>
                              <p className={styles.payments__title}>Express payment</p>
                              <div className={styles.topButtons}>
                                <PaymentButtons />
                              </div>
                            </div>
                            {!isIframeError && !tokens.length ? <HorizontalDivider>OR</HorizontalDivider> : null}
                            {withTokens ?
                              <>
                                <div className={styles.payments__cards}>
                                  <p className={styles.payments__title}>Your payment method</p>
                                  <CardSelect tokens={tokens} value={selectedToken} onChange={changeTokenHandler} />
                                </div>
                                <button className={styles.addNewCard} onClick={()=> addNewCardHandler(true)}>
                                  <PlusIcon /> Add new card
                                </button>
                                {isShowAutomatic ? <div className={styles.automaticPopup}>
                                  <Toggle
                                    value={isAutomaticTopUp}
                                    onClick={() => setIsAutomaticTopUp(!isAutomaticTopUp)}
                                  />
                                  Would you like to enable automatic top-up on the same amount if you
                                  have less than 20 GoCoins in your account?
                                </div> : null}
                                <ConfirmButton onClick={confirmPayHandler} />
                              </> :
                              <>
                                {
                                  newMethods[PaymentMethodType.CARD] ?
                                    <PayForm onCloseHandler={onCloseHandler} onErrorHandler={onErrorHandler} /> :
                                    <PayFrame maxPayRef={maxPayRef} iframeId={iframeId} isError={isIframeError} />
                                }
                              </>
                            }
                          </> : <>{
                            newMethods[PaymentMethodType.CARD] ?
                              <PayForm onCloseHandler={onCloseHandler} onErrorHandler={onErrorHandler} /> :
                              <PayFrame maxPayRef={maxPayRef} iframeId={iframeId} isError={isIframeError} />
                          }</>
                      }
                    </div>
                  </div>
                  <div className={styles.info}>
                    <CurrentPackage
                      openPackage={openPackage}
                      withSale={Boolean(withSale)}
                      isVisible
                    />
                    <p className={styles.infoRules}>
                      <InfoBlock />
                    </p>
                    <div className={styles.verifiedIcons}>
                      <MasterCardCheck />
                      <VisaSecureBlur />
                      <PciCertificateIcon />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}